import { ArrowBack, Check, Refresh } from '@mui/icons-material';
import { Box, IconButton, Paper, Typography, useColorScheme, CardMedia } from '@mui/material';
import { useLocation, useNavigate, useParams, useRevalidator } from 'react-router-dom';
import qs from 'qs';
import { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import BasicDataGridPro from '@/components/BasicDataGridPro';
import { GridColDef } from '@mui/x-data-grid-pro';
import { generateUUIDv4 } from '@/modules/MuiDialog/utils/uuid';
import DataGridNoRows from '@/components/common/DataGridNoRows';
import { LoadingButton } from '@/components/common/LoadingButton';
import delay from '@/utils/delay';
import { get } from 'lodash';
import { deleteCacheClear, getPartnersRevenueDetail } from '@/services/partners.service';

export default function PartnersSalesPage() {
    const navigate = useNavigate();
    const location = useLocation();

    return (
        <div className="flex flex-col flex-1 relative">
            <section className="flex items-center gap-2">
                <IconButton
                    onClick={() => {
                        navigate(-1);
                    }}
                >
                    <ArrowBack />
                </IconButton>
                <Typography variant="h3">파트너스 매출 관리</Typography>
            </section>
            <RevenueDetail />
        </div>
    );
}
function RevenueDetail() {
    const location = useLocation();
    const queryParams = qs.parse(location.search.split('?')[1] || '');
    const { product_image = '', product_name = '' } = location.state || {};
    const { userId = '' } = useParams();
    const { mode } = useColorScheme();
    const { revalidate } = useRevalidator();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    const productId = String(queryParams.productId || '');
    const [data, setData] = useState<RevenueDetailType | null>(null);
    const [offset, setOffset] = useState(0);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    useEffect(() => {
        (async () => {
            const result = await getPartnersRevenueDetail({
                user_id: userId,
                product_id: productId,
                offset,
                limit,
            });
            setData(result);
        })();
    }, [offset, limit]);

    const columns: GridColDef<any>[] = [
        {
            field: 'ordered_at',
            headerName: '주문일시',
            headerAlign: 'center',
            align: 'center',
            minWidth: 150,
            type: 'date',
            valueFormatter: (params) => {
                return moment(params?.value || '').format('YYYY-MM-DD HH:mm:ss');
            },
        },
        {
            field: 'product_name',
            headerName: '상품명',
            headerAlign: 'center',
            align: 'left',
            flex: 1,
            minWidth: 200,
        },
        {
            field: 'product_option_name',
            headerName: '옵션명',
            headerAlign: 'center',
            align: 'left',
            minWidth: 200,
            flex: 1,
            renderCell: ({ row, value }) => {
                return <Typography variant="body2">{get(row, 'product_name') === value ? '' : value}</Typography>;
            },
        },
        {
            field: 'orderer_name',
            headerName: '주문자명',
            headerAlign: 'center',
            align: 'center',
            minWidth: 135,
            maxWidth: 200,
        },
        {
            field: 'pay_amount',
            headerName: '결제금액',
            headerAlign: 'center',
            align: 'center',
            minWidth: 135,
            maxWidth: 200,
            valueFormatter: (params) => {
                return `${Number(params?.value || 0).toLocaleString()}원`;
            },
        },
        {
            field: 'order_status',
            headerName: '주문상태',
            headerAlign: 'center',
            align: 'center',
            minWidth: 135,
            maxWidth: 200,
        },
        {
            field: 'revenue_amount',
            headerName: '수익',
            headerAlign: 'center',
            align: 'center',
            minWidth: 135,
            maxWidth: 200,
            valueFormatter: (params) => {
                return `${Number(params?.value || 0).toLocaleString()}원`;
            },
        },
        {
            field: 'pending_amount',
            headerName: '미정산금액',
            headerAlign: 'center',
            align: 'center',
            minWidth: 135,
            maxWidth: 200,
            valueFormatter: (params) => {
                return `${Number(params?.value || 0).toLocaleString()}원`;
            },
        },
        {
            field: 'settled_amount',
            headerName: '정산금액',
            headerAlign: 'center',
            align: 'center',
            minWidth: 135,
            maxWidth: 200,
            valueFormatter: (params) => {
                return `${Number(params?.value || 0).toLocaleString()}원`;
            },
        },
        {
            field: 'settlement_status',
            headerName: '정산상태',
            headerAlign: 'center',
            align: 'center',
            minWidth: 135,
            maxWidth: 200,
            renderCell: (params) => {
                const value = params?.value || '';
                const isIcon = value === '정산완료';
                return (
                    <>
                        <Typography variant="body2">{value || ''}</Typography>
                        {!!isIcon && <Check color="success" sx={{ p: 0, m: 0 }} />}
                    </>
                );
            },
        },
    ];
    return (
        <section className="mt-5">
            {!!product_image && !!product_name && (
                <Box className="flex items-center" sx={{ maxWidth: '700px', mb: 3, gap: 2 }}>
                    <CardMedia
                        component="img"
                        image={product_image || ''}
                        alt={product_name || ''}
                        sx={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '5px',
                            objectFit: 'cover',
                            aspectRatio: '1 / 1',
                        }}
                    />
                    <Typography
                        variant="h6"
                        sx={{
                            whiteSpace: 'pre-wrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            wordBreak: 'break-all',
                            lineHeight: 1.3,
                        }}
                    >
                        {product_name}
                    </Typography>
                </Box>
            )}
            <section className="overallStatistics">
                <div className="flex justify-between">
                    <Typography variant="h3">전체 통계</Typography>
                    <LoadingButton
                        color={mode === 'light' ? 'black' : 'white'}
                        isLoading={isLoading}
                        startIcon={<Refresh />}
                        onClick={async () => {
                            setIsLoading(true);
                            await delay(500);
                            await deleteCacheClear({ type: 'REVENUE_DETAIL', userId });
                            revalidate();
                            setIsLoading(false);
                        }}
                    >
                        최근 업데이트: {moment(data?.last_updated_at || '').format('YYYY-MM-DD HH:mm')}
                    </LoadingButton>
                </div>
                <Paper sx={{ mt: 3 }}>
                    <Box
                        className="w-full border rounded-xl"
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            p: 2,
                            borderRadius: '5px',
                            rowGap: 2,
                        }}
                    >
                        <PartnersSummaryItem
                            title="총 수익"
                            value={Number(data?.overall_statistics?.total_revenue_amount || 0).toLocaleString()}
                        />
                        <PartnersSummaryItem
                            title="총 미정산"
                            value={Number(data?.overall_statistics?.total_pending_amount || 0).toLocaleString()}
                        />
                        <PartnersSummaryItem
                            title="총 정산"
                            value={Number(data?.overall_statistics?.total_settled_amount || 0).toLocaleString()}
                        />
                    </Box>
                </Paper>
            </section>
            <section
                className="orderList"
                style={{
                    marginTop: '2rem',
                }}
            >
                <Typography variant="h3">주문 목록</Typography>
                <Paper sx={{ mt: 3 }}>
                    <BasicDataGridPro
                        disableColumnReorder
                        rowHeight={Number(data?.total_count || 0) > 0 ? 50 : 150}
                        disableColumnMenu={true}
                        rowCount={Number(data?.total_count || 0)}
                        getRowId={(row) => row?.order_number || generateUUIDv4()}
                        columns={columns}
                        rows={[...(data?.order_list || [])]}
                        pagination
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                backgroundColor: mode === 'light' ? '#F5F5F5' : '#2c2c2c',
                            },
                        }}
                        slots={{
                            noRowsOverlay: () =>
                                DataGridNoRows({
                                    type: 'basic',
                                    description: '아직 주문 목록이 없어요!',
                                }),
                        }}
                        paginationMode="server"
                        pageSizeOptions={[5, 10, 25]}
                        onPaginationModelChange={async (model) => {
                            setPage(model.page);
                            setOffset(model.page * limit);
                            setLimit(model.pageSize);
                        }}
                        paginationModel={{
                            page: offset !== 0 ? offset / limit : 0,
                            pageSize: limit,
                        }}
                    />
                </Paper>
            </section>
        </section>
    );
}

function PartnersSummaryItem({ title, value }: { title: string; value: string }) {
    return (
        <Box className="w-full flex flex-col gap-1 justify-center">
            <Typography variant="body2" color="text.secondary" fontWeight={400}>
                {title}
            </Typography>
            <Typography variant="h5" component={'span'} fontWeight={500}>
                {value}원
            </Typography>
        </Box>
    );
}
interface RevenueOrderType {
    ordered_at: string;
    order_number: string;
    orderer_name: string;
    order_status: string;
    product_name: string;
    product_option_name: string;
    pay_amount: number;
    revenue_amount: number;
    pending_amount: number;
    settled_amount: number;
    settlement_status: string;
}
interface RevenueDetailType {
    overall_statistics: {
        total_revenue_amount: number;
        total_pending_amount: number;
        total_settled_amount: number;
    };
    order_list: RevenueOrderType[];
    total_count: number;
    last_updated_at: string;
}
const dummy: RevenueDetailType = {
    overall_statistics: {
        total_revenue_amount: 10000000,
        total_pending_amount: 10000000,
        total_settled_amount: 10000000,
    },
    order_list: [
        {
            ordered_at: moment().format('YYYY-MM-DD HH:mm:ss'),
            order_number: generateUUIDv4(),
            orderer_name: '홍길동',
            order_status: '주문확인완료',
            product_name: '상품 1',
            product_option_name: '상품 옵션 1',
            pay_amount: 10000000,
            revenue_amount: 10000000,
            pending_amount: 10000000,
            settled_amount: 10000000,
            settlement_status: '미정산',
        },
        {
            ordered_at: moment().format('YYYY-MM-DD HH:mm:ss'),
            order_number: generateUUIDv4(),
            orderer_name: '홍길동',
            order_status: '주문확인완료',
            product_name: '상품 2',
            product_option_name: '상품 옵션 2',
            pay_amount: 10000000,
            revenue_amount: 10000000,
            pending_amount: 10000000,
            settled_amount: 10000000,
            settlement_status: '정산완료',
        },
        {
            ordered_at: moment().format('YYYY-MM-DD HH:mm:ss'),
            order_number: generateUUIDv4(),
            orderer_name: '홍길동',
            order_status: '주문확인완료',
            product_name: '상품 3',
            product_option_name: '상품 옵션 3',
            pay_amount: 10000000,
            revenue_amount: 10000000,
            pending_amount: 10000000,
            settled_amount: 10000000,
            settlement_status: '미정산',
        },
        {
            ordered_at: moment().format('YYYY-MM-DD HH:mm:ss'),
            order_number: generateUUIDv4(),
            orderer_name: '홍길동',
            order_status: '주문확인완료',
            product_name: '상품 4',
            product_option_name: '상품 옵션 4',
            pay_amount: 10000000,
            revenue_amount: 10000000,
            pending_amount: 10000000,
            settled_amount: 10000000,
            settlement_status: '미정산',
        },
        {
            ordered_at: moment().format('YYYY-MM-DD HH:mm:ss'),
            order_number: generateUUIDv4(),
            orderer_name: '홍길동',
            order_status: '주문확인완료',
            product_name: '상품 5',
            product_option_name: '상품 옵션 5',
            pay_amount: 10000000,
            revenue_amount: 10000000,
            pending_amount: 10000000,
            settled_amount: 10000000,
            settlement_status: '미정산',
        },
    ],
    total_count: 5,
    last_updated_at: moment().toISOString(),
};
